import { useMemo } from 'react';

import { useTheme, readableColor, createLocalStorageManager, lighten } from '@vimeo/bokeh';
import { extendTheme, bokehTheme } from '@vimeo/bokeh-theme';
import { STORAGE_KEY } from '@vimeo/core/components/BokehProvider';

import { type AccentColor } from './type';

/**
 * Take a color string as accent color, if no accent color is provided, return the default Bokeh theme.
 */
export const useAccentTheme = (accentColor?: AccentColor) => {
  const contextBokehTheme = useTheme();

  const accentTheme = useMemo(() => {
    if (!accentColor) return contextBokehTheme;

    /**
     * Extended theme has to be based on `bokehTheme` not the `contextBokehTheme`, or it will cause mutation for some reason.
     * Even worse, deep clone the `contextBokehTheme` will cause some portal-based component lose bounds...
     */
    return extendTheme(
      {
        colors: {
          accent: accentColor,
          accentTextColor: readableColor(accentColor),
        },
        components: {
          Button: {
            variants: {
              accent: {
                color: readableColor(accentColor),
                bgColor: accentColor,
                _hover: {
                  bgColor: lighten(accentColor, 0.1),
                },
                _dark: {
                  bgColor: accentColor,
                  _hover: {
                    bgColor: lighten(accentColor, 0.1),
                  },
                },
              },
            },
          },
        },
      },
      bokehTheme,
    );
  }, [accentColor, contextBokehTheme]);

  /** This is to sync theme preference with global theme */
  const colorModeManager = createLocalStorageManager(STORAGE_KEY);

  return { theme: accentTheme, colorModeManager };
};
